import type { Client, Conversation, Message } from '@twilio/conversations';

import {
  ChatTransportError,
  type ChatConversation,
  type ChatConversationLastMessage,
  type ChatConversationStatus,
  type ChatMessage,
  type ChatMessageListener,
  type ChatMessagePage,
  type ChatTransport,
  type OutgoingChatMessage,
  type Unsubscribe,
} from './chatTransport';
import type { ChatEventSource, InboxConversation } from './inboxSync';
import type { PostChatMessage } from './postInquiryMessage';

/**
 * Twilio Conversations SDK error codes.
 * @see https://www.twilio.com/docs/api/errors
 */
const TWILIO_ERROR_CODES = {
  CONVERSATION_NOT_FOUND: 50300,
  ACCESS_FORBIDDEN: 54007,
} as const;

function toChatTransportError(error: unknown): ChatTransportError {
  const message = error instanceof Error ? error.message : 'Unknown error';

  if (error && typeof error === 'object') {
    const errorObj = error as { code?: number; status?: number };

    if (errorObj.code === TWILIO_ERROR_CODES.CONVERSATION_NOT_FOUND || errorObj.status === 404) {
      return new ChatTransportError('not_found', message, error);
    }

    if (errorObj.code === TWILIO_ERROR_CODES.ACCESS_FORBIDDEN || errorObj.status === 403) {
      return new ChatTransportError('forbidden', message, error);
    }
  }

  return new ChatTransportError('connection', message, error);
}

export function toChatMessage(message: Message): ChatMessage {
  return {
    id: message.sid,
    index: message.index,
    author: message.author,
    body: message.body,
    attributes: message.attributes,
    createdAt: message.dateCreated,
    updatedAt: message.dateUpdated,
  };
}

/**
 * The SDK fields both the chat and the inbox read, mapped once so the two
 * cannot drift apart.
 */
function lastMessageOf(conversation: Conversation): ChatConversationLastMessage | undefined {
  const last = conversation.lastMessage;

  return last ? { index: last.index, createdAt: last.dateCreated } : undefined;
}

function statusOf(conversation: Conversation): ChatConversationStatus | undefined {
  return conversation.state?.current;
}

/**
 * A Twilio conversation behind the seam.
 *
 * Read-state and status are getters over the SDK object rather than copies,
 * because the SDK mutates the conversation in place as events arrive and the
 * chat reads them at the moment it decides.
 */
class TwilioChatConversation implements ChatConversation {
  /** Twilio has no idempotency key for a message, and neither does the endpoint in front of it yet. */
  readonly capabilities = { idempotentSend: false };

  constructor(
    private readonly conversation: Conversation,
    private readonly post: PostChatMessage
  ) {}

  get id(): string {
    return this.conversation.sid;
  }

  get attributes(): unknown {
    return this.conversation.attributes;
  }

  get lastReadIndex(): number | null {
    return this.conversation.lastReadMessageIndex;
  }

  get lastMessage(): ChatConversationLastMessage | undefined {
    return lastMessageOf(this.conversation);
  }

  get status(): ChatConversationStatus | undefined {
    return statusOf(this.conversation);
  }

  async getMessages(pageSize?: number, anchor?: number): Promise<ChatMessagePage> {
    const page = await this.conversation.getMessages(pageSize, anchor);

    return { items: page.items.map(toChatMessage), hasPrevPage: page.hasPrevPage };
  }

  async markAllRead(): Promise<void> {
    await this.conversation.setAllMessagesRead();
  }

  /** Through our API, as before; the stored message arrives as the SDK's `messageAdded`. */
  async send(message: OutgoingChatMessage): Promise<undefined> {
    await this.post(this.id, message);

    return undefined;
  }

  onMessageAdded(listener: ChatMessageListener): Unsubscribe {
    const handler = (message: Message) => listener(toChatMessage(message));

    this.conversation.on('messageAdded', handler);

    return () => {
      this.conversation.off('messageAdded', handler);
    };
  }

  onMessageUpdated(listener: ChatMessageListener): Unsubscribe {
    const handler = ({ message }: { message: Message }) => listener(toChatMessage(message));

    this.conversation.on('messageUpdated', handler);

    return () => {
      this.conversation.off('messageUpdated', handler);
    };
  }
}

/**
 * The Twilio Conversations client behind the seam.
 *
 * Each SDK conversation keeps one wrapper, so a chat that opens the same
 * conversation twice gets the same object back, as it did when the SDK's own
 * conversation was handed out. Effects keyed on the conversation rely on that.
 *
 * `client` stays reachable for the session code that owns the SDK's lifecycle:
 * token refresh, listener teardown and the client-wide events. Nothing else
 * should read it.
 */
export class TwilioChatTransport implements ChatTransport {
  private readonly wrappers = new WeakMap<Conversation, TwilioChatConversation>();

  constructor(
    readonly client: Client,
    private readonly post: PostChatMessage
  ) {}

  async getConversation(conversationId: string): Promise<ChatConversation> {
    let conversation: Conversation;

    try {
      conversation = await this.client.peekConversationBySid(conversationId);
    } catch (error) {
      throw toChatTransportError(error);
    }

    let wrapper = this.wrappers.get(conversation);

    if (!wrapper) {
      wrapper = new TwilioChatConversation(conversation, this.post);

      this.wrappers.set(conversation, wrapper);
    }

    return wrapper;
  }

  /** Not `async`: the session code calls this inside a `try`, which must still see a synchronous throw. */
  shutdown(): Promise<void> {
    return this.client.shutdown();
  }
}

/** The SDK client behind a transport, when the transport is Twilio's. */
export function twilioClientOf(transport: ChatTransport | undefined): Client | undefined {
  return transport instanceof TwilioChatTransport ? transport.client : undefined;
}

function toInboxConversation(conversation: Conversation): InboxConversation {
  return {
    id: conversation.sid,
    lastMessage: lastMessageOf(conversation),
    status: statusOf(conversation),
  };
}

/**
 * The client-wide conversation events of a Twilio session, as inbox events.
 *
 * Each conversation is read when its event arrives, which is when the SDK has
 * applied the change the event reports.
 */
export function twilioInboxEvents(client: Client): ChatEventSource {
  return {
    get viewerIdentity() {
      return client.user?.identity;
    },

    subscribe(listener) {
      const onConversationUpdated = ({ conversation }: { conversation: Conversation }) =>
        listener({ type: 'conversationUpdated', conversation: toInboxConversation(conversation) });

      const onConversationRemoved = (conversation: Conversation) =>
        listener({ type: 'conversationRemoved', conversation: toInboxConversation(conversation) });

      const onConversationJoined = (conversation: Conversation) =>
        listener({ type: 'conversationJoined', conversation: toInboxConversation(conversation) });

      const onMessageAdded = (message: Message) =>
        listener({
          type: 'messageAdded',
          conversation: toInboxConversation(message.conversation),
          message: toChatMessage(message),
        });

      const onMessageUpdated = ({ message }: { message: Message }) =>
        listener({
          type: 'messageUpdated',
          conversation: toInboxConversation(message.conversation),
          message: toChatMessage(message),
        });

      client.on('conversationUpdated', onConversationUpdated);

      client.on('conversationRemoved', onConversationRemoved);

      client.on('conversationJoined', onConversationJoined);

      client.on('messageAdded', onMessageAdded);

      client.on('messageUpdated', onMessageUpdated);

      return () => {
        client.off('conversationUpdated', onConversationUpdated);

        client.off('conversationRemoved', onConversationRemoved);

        client.off('conversationJoined', onConversationJoined);

        client.off('messageAdded', onMessageAdded);

        client.off('messageUpdated', onMessageUpdated);
      };
    },
  };
}
